export const deg2rad = (deg: number) => {
  return deg * (Math.PI / 180);
};

const rad2deg = (rad: number) => {
  return rad * (180 / Math.PI);
};

const EARTH_RADIUS_M = 6371e3;

export const getDistanceBetweenPoints = (
  coord1: [number, number],
  coord2: [number, number]
) => {
  const dLat = deg2rad(coord1[0] - coord2[0]);
  const dLon = deg2rad(coord1[1] - coord2[1]);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(deg2rad(coord1[0])) *
      Math.cos(deg2rad(coord2[0])) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  const d = EARTH_RADIUS_M * c; // Distance in m
  return Math.round(d);
};

/**
 * Great-circle destination point from a start lat/lon, bearing and distance.
 * Uses the spherical law of navigation (not a flat-earth/equirectangular
 * approximation), so it stays accurate at high latitudes where meridians
 * converge and a naive lon-delta calculation would distort or blow up.
 * Returns [lon, lat] to match Deck.gl's coordinate convention.
 */
export const destinationPoint = (
  lat: number,
  lon: number,
  bearingDeg: number,
  distanceMeters: number
): [number, number] => {
  const angularDistance = distanceMeters / EARTH_RADIUS_M;
  const bearingRad = deg2rad(bearingDeg);
  const lat1 = deg2rad(lat);
  const lon1 = deg2rad(lon);

  const lat2 = Math.asin(
    Math.sin(lat1) * Math.cos(angularDistance) +
      Math.cos(lat1) * Math.sin(angularDistance) * Math.cos(bearingRad)
  );
  const lon2 =
    lon1 +
    Math.atan2(
      Math.sin(bearingRad) * Math.sin(angularDistance) * Math.cos(lat1),
      Math.cos(angularDistance) - Math.sin(lat1) * Math.sin(lat2)
    );

  const normalizedLonDeg = ((rad2deg(lon2) + 540) % 360) - 180;
  return [normalizedLonDeg, rad2deg(lat2)];
};
